/**
 * The user's mic/sound state, one value everywhere.
 *
 * Stored in localStorage so it holds out of voice (you walk in already
 * silent), across channel switches and reconnects. The toggles in
 * voiceCommands write it in and out of a call; `voice:join` carries it so
 * the server starts the session in that state.
 *
 * Single-tab subscription via a custom event so the widget can mirror
 * the value across renders without polling.
 */

const KEY_PRE_MUTE = 'bunker:voice-pre-mute';
const KEY_PRE_DEAF = 'bunker:voice-pre-deaf';
const EVT_CHANGE = 'bunker:voice-prefs-change';

function read(key: string): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(key) === '1';
}

function write(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  if (value) window.localStorage.setItem(key, '1');
  else window.localStorage.removeItem(key);
  window.dispatchEvent(new Event(EVT_CHANGE));
}

export function getPreMute(): boolean {
  return read(KEY_PRE_MUTE);
}

export function setPreMute(value: boolean): void {
  write(KEY_PRE_MUTE, value);
}

export function getPreDeaf(): boolean {
  return read(KEY_PRE_DEAF);
}

export function setPreDeaf(value: boolean): void {
  // Independent toggles: deafening does NOT flip the mic (the owner found
  // the linked Discord behaviour confusing). A deafened user still never
  // transmits — voiceMedia stops the mic while deafened.
  write(KEY_PRE_DEAF, value);
}

/**
 * Subscribe to pref changes. Returns an unsubscribe function.
 */
export function subscribeVoicePrefs(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVT_CHANGE, listener);
  return () => window.removeEventListener(EVT_CHANGE, listener);
}
